import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "./common";

const inputCls = "w-full h-9 rounded-lg border border-zinc-200 bg-white px-3 text-sm font-normal text-zinc-900 transition-colors hover:border-zinc-300 focus:border-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/5";

function Field({ f, value, set, testid }) {
  if (f.type === "file") return (
    <div className="space-y-1.5">
      <input data-testid={testid} type="file" multiple accept={f.accept} onChange={(e) => set([...(value || []), ...e.target.files])}
        className="block w-full text-xs font-normal text-zinc-600 file:mr-3 file:rounded-lg file:border-0 file:bg-violet-50 file:px-3 file:py-2 file:text-xs file:font-semibold file:text-violet-700 hover:file:bg-violet-100" />
      {(value || []).map((x, i) => (
        <div key={i} className="flex items-center justify-between rounded-md bg-zinc-50 px-2.5 py-1.5 text-xs font-normal text-zinc-700">
          <span className="truncate">{x.name}</span>
          <button type="button" data-testid={`${testid}-remove-${i}`} onClick={() => set(value.filter((_, j) => j !== i))} className="ml-2 text-zinc-400 hover:text-rose-600">Remove</button>
        </div>
      ))}
      {f.hint && <div className="text-[11px] font-normal text-zinc-400">{f.hint}</div>}
    </div>
  );
  if (f.type === "select") return <NativeSelect className="w-full" testid={testid} value={value ?? ""} onChange={set} options={f.options} placeholder="Select…" />;
  if (f.type === "textarea") return <textarea data-testid={testid} value={value ?? ""} onChange={(e) => set(e.target.value)} rows={3} className={`${inputCls} h-auto py-2`} />;
  return <input data-testid={testid} type={f.type || "text"} value={value ?? ""} onChange={(e) => set(f.type === "number" ? Number(e.target.value) : e.target.value)} className={inputCls} />;
}

export function FormDialog({ open, onOpenChange, title, fields, initial, onSubmit, testid = "form", submitLabel = "Save", wide = false }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(initial || {}); }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await onSubmit(form);
    setBusy(false);
    if (ok !== false) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} data-testid={`${testid}-dialog`} className={`${wide ? "max-w-3xl" : "max-w-xl"} max-h-[90vh] overflow-y-auto`}>
        <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {fields.map((f) => f.type === "section" ? (
            <div key={f.name} data-testid={`${testid}-section-${f.name.replace(/^_/, "")}`} className="sm:col-span-2 -mb-1 mt-2 border-b border-zinc-200 pb-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-[#15966B]">{f.label}</div>
          ) : (
            <label key={f.name} className={`text-xs font-medium text-slate-600 space-y-1.5 ${f.full || f.type === "textarea" || f.type === "file" ? "sm:col-span-2" : ""}`}>
              <span>{f.label}{f.required && <span className="text-rose-500"> *</span>}</span>
              <Field f={f} value={form[f.name]} set={(v) => setForm((s) => ({ ...s, [f.name]: v }))} testid={`${testid}-${f.name}-input`} />
            </label>
          ))}
          <DialogFooter className="sm:col-span-2 mt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid={`${testid}-cancel-button`}>Cancel</Button>
            <Button
              type="submit"
              disabled={busy || fields.some((f) => f.required && !form[f.name])}
              data-testid={`${testid}-submit-button`}
            >
              {busy ? "Saving…" : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
